import React from 'react';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Chip,
  Stack,
} from '@mui/material';
import {
  Phone as PhoneIcon,
  Email as EmailIcon,
  Person as PersonIcon,
  Sms as SmsIcon,
  VideoCall as VideoIcon,
  MoreHoriz as MoreIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useFormatInstant } from '../../hooks/useClinicTimezone';
import { ClientCommunication } from './ClientCommunicationDialog';

interface ClientCommunicationListProps {
  communications: ClientCommunication[];
  onDelete?: (id: string) => void;
}

const getTypeIcon = (type: string) => {
  switch (type.toLowerCase()) {
    case 'phone':
      return <PhoneIcon fontSize="small" />;
    case 'email':
    case 'stabledocs':
      return <EmailIcon fontSize="small" />;
    case 'in-person':
      return <PersonIcon fontSize="small" />;
    case 'text message':
      return <SmsIcon fontSize="small" />;
    case 'video call':
      return <VideoIcon fontSize="small" />;
    default:
      return <MoreIcon fontSize="small" />;
  }
};

const getTypeColor = (type: string): 'default' | 'primary' | 'secondary' | 'success' | 'info' => {
  switch (type.toLowerCase()) {
    case 'phone':
      return 'primary';
    case 'email':
    case 'stabledocs':
      return 'info';
    case 'in-person':
      return 'success';
    case 'text message':
      return 'secondary';
    default:
      return 'default';
  }
};

// Entries a server job wrote carry the job's id as "Logged by"; staff see who that is.
const SYSTEM_AUTHORS: Record<string, string> = { 'system:reminders': 'Automatic reminder' };

// What happened to the email an entry logged (INV-125), as Postmark reported it.
const EMAIL_STATUS: Record<string, { label: string; color: 'default' | 'success' | 'info' | 'error' }> = {
  sent: { label: 'Sent', color: 'default' },
  delivered: { label: 'Delivered', color: 'success' },
  opened: { label: 'Opened', color: 'info' },
  bounced: { label: 'Bounced', color: 'error' },
  failed: { label: 'Failed', color: 'error' },
};

const ClientCommunicationList: React.FC<ClientCommunicationListProps> = ({
  communications,
  onDelete,
}) => {
  // ClientCommunication.dateTime is an INSTANT — the dialog's DateTimePicker posts
  // `toISOString()` (a picked date + time), and the schema default is now()
  // (DATE-KINDS §1.3). Rendered in the CLINIC's zone, never the browser's.
  const formatInstant = useFormatInstant();

  // Opened and Bounced carry their moment; nothing stores when an email was delivered.
  const emailStatusLabel = (email: NonNullable<ClientCommunication['outboundEmail']>) => {
    const word = EMAIL_STATUS[email.status]?.label ?? email.status;
    const at = email.status === 'opened' ? email.openedAt : email.status === 'bounced' ? email.bouncedAt : null;
    return at ? `${word} ${formatInstant(at)}` : word;
  };

  if (communications.length === 0) {
    return (
      <Box py={6} textAlign="center">
        <Typography variant="h6" color="textSecondary" gutterBottom>
          No communications logged yet
        </Typography>
        <Typography color="textSecondary">
          Click "Log Communication" to add your first entry
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {communications.map((comm) => (
        <Paper key={comm.id} sx={{ p: 2 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box flex={1}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} mb={1}>
                <Chip
                  icon={getTypeIcon(comm.type)}
                  label={comm.type}
                  size="small"
                  color={getTypeColor(comm.type)}
                />
                <Typography variant="body2" color="textSecondary" data-testid="communication-datetime">
                  {formatInstant(comm.dateTime)}
                </Typography>
                {/* Keyed on the link, not the type: staff can edit an entry's type. */}
                {comm.outboundEmail && (
                  <Chip
                    label={emailStatusLabel(comm.outboundEmail)}
                    size="small"
                    variant="outlined"
                    color={EMAIL_STATUS[comm.outboundEmail.status]?.color ?? 'default'}
                    data-testid="communication-email-status"
                  />
                )}
              </Box>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {comm.notes}
              </Typography>
              {comm.staffMember && (
                <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                  Logged by: {SYSTEM_AUTHORS[comm.staffMember] ?? comm.staffMember}
                </Typography>
              )}
            </Box>
            {onDelete && (
              <IconButton
                size="small"
                onClick={() => onDelete(comm.id)}
                color="error"
                title="Delete communication"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Paper>
      ))}
    </Stack>
  );
};

export default ClientCommunicationList;

